import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, Check, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { SC_SCRIPT_TYPES, SC_LANGUAGES } from '../constants';

const Sidebar = ({
  scLayer,
  setScLayer,
  scScriptType,
  setScScriptType,
  scType,
  setScType,
  scSafeScripts,
  setScSafeScripts,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isScriptTypeOpen, setIsScriptTypeOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const scriptTypeRef = useRef(null);
  const languageRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scriptTypeRef.current && !scriptTypeRef.current.contains(event.target)) {
        setIsScriptTypeOpen(false);
      }
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      className="scriptly-config-sidebar transition-all duration-300 flex flex-col"
      style={{ width: isExpanded ? 260 : 48, padding: isExpanded ? '20px 16px 50px 24px' : '0', overflowX: 'hidden' }}
    >
      <button
        type="button"
        className={`panel-sub-header flex items-center ${isExpanded ? 'justify-between mb-4' : 'justify-center mt-4 mb-4'} cursor-pointer hover:bg-slate-50 transition-colors w-full`}
        style={{ padding: isExpanded ? '0' : '8px', margin: isExpanded ? '0 0 16px 0' : '16px auto', border: 'none', background: 'transparent' }}
        onClick={() => setIsExpanded(!isExpanded)}
        title={isExpanded ? "Collapse Configuration" : "Expand Configuration"}
      >
        {isExpanded ? (
          <>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Configuration Selection</span>
            <div className="flex items-center gap-2 shrink-0">
              <PanelLeftClose size={14} className="text-slate-400 hover:text-blue-500 transition-colors" />
            </div>
          </>
        ) : (
          <PanelLeftOpen size={14} className="text-slate-400 hover:text-blue-500 transition-colors shrink-0 translate-x-1.5" />
        )}
      </button>

      {isExpanded && (
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto custom-scrollbar" style={{ opacity: isExpanded ? 1 : 0, transition: 'opacity 0.2s ease', margin: '0 -16px 0 -24px', padding: '0 16px 0 24px' }}>
          <div className="flex items-center gap-2 mb-4 mt-2">
            <span className="text-[11px] font-semibold text-slate-500">Script Generation</span>
            <div className="layer-toggle flex-1 ml-2" style={{ marginBottom: 0 }}>
              <button
                type="button"
                className={`toggle-btn ${scLayer === 'Extract' ? 'active' : ''}`}
                onClick={() => setScLayer('Extract')}
              >
                Extract
              </button>
              <button
                type="button"
                className={`toggle-btn ${scLayer === 'Transform' ? 'active' : ''}`}
                onClick={() => setScLayer('Transform')}
              >
                Transform
              </button>
            </div>
          </div>

          <div className="mb-6 flex flex-col gap-2">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Script Type</span>
            <div className="relative" ref={scriptTypeRef}>
              <button
                type="button"
                className={`flex items-center justify-between w-full px-3 py-2.5 bg-[#ffffff] hover:bg-slate-50 border ${isScriptTypeOpen ? 'border-blue-500/50 ring-1 ring-blue-500/20' : 'border-slate-200'} hover:border-blue-500/50 rounded-lg text-[13px] font-medium text-slate-700 transition-all shadow-sm outline-none cursor-pointer`}
                onClick={() => setIsScriptTypeOpen(!isScriptTypeOpen)}
              >
                <span className="truncate">{scScriptType}</span>
                <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 flex-shrink-0 ${isScriptTypeOpen ? "rotate-180 text-blue-500" : ""}`} />
              </button>

              {isScriptTypeOpen && (
                <div role="menu" className="absolute top-full left-0 right-0 mt-2 bg-[#ffffff] border border-slate-200 rounded-lg shadow-2xl z-50 py-1.5 overflow-hidden ring-1 ring-black/10">
                  {SC_SCRIPT_TYPES.map(type => (
                    <div
                      key={type}
                      className={`px-3 py-2.5 text-[13px] cursor-pointer transition-colors flex items-center justify-between ${scScriptType === type ? 'bg-blue-50 text-blue-600 font-semibold border-l-2 border-blue-500' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-2 border-transparent'}`}
                      onClick={() => {
                        setScScriptType(type);
                        setIsScriptTypeOpen(false);
                      }}
                      role="menuitem"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setScScriptType(type);
                          setIsScriptTypeOpen(false);
                        }
                      }}
                    >
                      {type}
                      {scScriptType === type && <Check size={12} className="text-blue-500" />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>



          <div className="mb-6 flex flex-col gap-2">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Script Language</span>
            <div className="relative" ref={languageRef}>
              <button
                type="button"
                className={`flex items-center justify-between w-full px-3 py-2.5 bg-[#ffffff] hover:bg-slate-50 border ${isLanguageOpen ? 'border-blue-500/50 ring-1 ring-blue-500/20' : 'border-slate-200'} hover:border-blue-500/50 rounded-lg text-[13px] font-medium text-slate-700 transition-all shadow-sm outline-none cursor-pointer`}
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
              >
                <span className="truncate">{scType}</span>
                <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 flex-shrink-0 ${isLanguageOpen ? "rotate-180 text-blue-500" : ""}`} />
              </button>

              {isLanguageOpen && (
                <div role="menu" className="absolute top-full left-0 right-0 mt-2 bg-[#ffffff] border border-slate-200 rounded-lg shadow-2xl z-50 py-1.5 overflow-hidden ring-1 ring-black/10">
                  {SC_LANGUAGES.map(lang => (
                    <div
                      key={lang}
                      className={`px-3 py-2.5 text-[13px] cursor-pointer transition-colors flex items-center justify-between ${scType === lang ? 'bg-blue-50 text-blue-600 font-semibold border-l-2 border-blue-500' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-2 border-transparent'}`}
                      onClick={() => {
                        setScType(lang);
                        setIsLanguageOpen(false);
                      }}
                      role="menuitem"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setScType(lang);
                          setIsLanguageOpen(false);
                        }
                      }}
                    >
                      {lang}
                      {scType === lang && <Check size={12} className="text-blue-500" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

Sidebar.propTypes = {
  scLayer: PropTypes.string,
  setScLayer: PropTypes.func,
  scScriptType: PropTypes.string,
  setScScriptType: PropTypes.func,
  scType: PropTypes.string,
  setScType: PropTypes.func,
  scSafeScripts: PropTypes.bool,
  setScSafeScripts: PropTypes.func,
};

export default Sidebar;
